import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import WarningIcon from '@assets/icons/warning.svg';
import { ApplicationType } from '@/contexts/dynamic-config';
import { createStyles } from '@/contexts/theme-context';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { TouchableOpacity } from 'react-native';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useUpdateOrderGuideItemContext } from '@/contexts/update-order-guide-item-context';

interface Props {
  itemMetadata: ItemListOptionMetadata;
  variantPackUUID?: string;
}

const HIT_SLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};
export const EssentialItemButton: React.FC<Props> = ({
  itemMetadata,
  variantPackUUID,
}) => {
  const { editEssentialItem, isRowEssential } =
    useUpdateOrderGuideItemContext();
  const [styles, theme] = useStyles();

  const isEssential = useMemo(() => {
    return isRowEssential(itemMetadata, variantPackUUID);
  }, [itemMetadata, isRowEssential, variantPackUUID]);
  const isCatalogLimited =
    useDynamicConfigValue('application_type') === ApplicationType.CATALOG_ONLY;
  if (isCatalogLimited) {
    return null;
  }

  return (
    <TouchableOpacity
      accessibilityLabel={
        isEssential
          ? i18n.t('unmark_as_essential')
          : i18n.t('mark_as_essential')
      }
      accessibilityRole="button"
      focusable={false}
      hitSlop={HIT_SLOP}
      onPress={() => {
        Analytics.track('essential_item_button.press', {
          variantPackUUIDs: itemMetadata.skus.map((sku) => sku.variantPackUUID),
        });
        editEssentialItem(itemMetadata, variantPackUUID);
      }}
      style={[
        styles.base,
        isEssential ? styles.baseEssential : styles.baseStandard,
      ]}
      testID={`essential-item-button-${isEssential ? 'enabled' : 'disabled'}`}
    >
      <WarningIcon
        fill={isEssential ? theme.colors.white : theme.colors.inactiveText}
        height={16}
        stroke={isEssential ? theme.colors.white : theme.colors.inactiveText}
        width={16}
      />
    </TouchableOpacity>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
    borderRadius: 12,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  baseEssential: {
    backgroundColor: theme.colors.warning,
  },
  baseStandard: {
    backgroundColor: theme.colors.background,
  },
}));
